import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  MessageSquare,
  RefreshCw,
  Send,
  ArrowLeft,
  Settings,
  Star,
  CheckCircle2,
} from "lucide-react";
import { SiFacebook, SiInstagram } from "react-icons/si";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Link } from "wouter";
import PartnerLayout from "./PartnerLayout";

type Platform = "all" | "facebook" | "instagram" | "eatzy";

interface InboxThread {
  platform: "facebook" | "instagram" | "eatzy";
  threadId: string;
  connectionId?: string;
  pageId?: string;
  pageName?: string;
  igUserId?: string;
  customerName: string;
  customerHandle: string;
  preview: string;
  createdAt: string;
  postMessage?: string;
  rating?: number | null;
  hasReply?: boolean;
  raw?: Record<string, unknown>;
}

interface InboxData {
  threads: InboxThread[];
  connectionCount: number;
  connected: {
    facebook: boolean;
    instagram: boolean;
  };
}

// ─── Typed API response shapes ────────────────────────────────────────────────

interface ThreadMessage {
  id: string;
  message: string;
  from?: { id: string; name?: string; username?: string } | null;
  created_time: string;
  isPost?: boolean;
}

interface SocialThreadApiData {
  id: string;
  message?: string;
  from?: { id: string; name: string } | null;
  created_time: string;
  comments?: { data: ThreadMessage[] };
  messages?: { data: ThreadMessage[] };
  participants?: { data: { id: string; name?: string; username?: string }[] };
}

interface SocialThreadDetailResponse {
  platform: string;
  thread: SocialThreadApiData;
}

interface EatzyReviewReply {
  id: string;
  text: string;
  createdAt: string;
}

interface EatzyThreadApiData {
  id: string;
  rating: number | null;
  text: string | null;
  createdAt: string;
  customerName: string;
  customerHandle: string;
  customerAvatar?: string;
  reply: EatzyReviewReply | null;
}

interface EatzyThreadDetailResponse {
  platform: string;
  thread: EatzyThreadApiData;
}

// ─────────────────────────────────────────────────────────────────────────────

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
}

function PlatformIcon({ platform, size = 12 }: { platform: "facebook" | "instagram" | "eatzy"; size?: number }) {
  if (platform === "facebook") {
    return <SiFacebook style={{ width: size, height: size }} className="text-blue-600" />;
  }
  if (platform === "instagram") {
    return <SiInstagram style={{ width: size, height: size }} className="text-pink-600" />;
  }
  return <Star style={{ width: size, height: size }} className="text-primary" />;
}

function StarRatingDisplay({ rating }: { rating: number | null | undefined }) {
  if (rating == null) return null;
  const out10 = rating;
  const stars = Math.round(out10 / 2);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          className={`h-3 w-3 ${i <= stars ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`}
        />
      ))}
      <span className="text-xs text-muted-foreground ml-1">{out10}/10</span>
    </div>
  );
}

function ThreadCard({
  thread,
  isSelected,
  onClick,
}: {
  thread: InboxThread;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left flex items-start gap-3 px-4 py-3.5 border-b last:border-b-0 hover-elevate transition-colors ${
        isSelected ? "bg-muted/60" : ""
      }`}
      data-testid={`inbox-thread-${thread.threadId}`}
    >
      <div className="relative shrink-0">
        <Avatar className="h-10 w-10">
          <AvatarFallback className="text-sm font-semibold">
            {getInitials(thread.customerName)}
          </AvatarFallback>
        </Avatar>
        <span className="absolute -bottom-0.5 -right-0.5 rounded-full bg-background p-0.5">
          <PlatformIcon platform={thread.platform} size={11} />
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline justify-between gap-2 mb-0.5">
          <span className="font-semibold text-sm truncate">{thread.customerName}</span>
          <span className="text-xs text-muted-foreground shrink-0">
            {formatRelativeTime(thread.createdAt)}
          </span>
        </div>
        {thread.platform === "eatzy" && thread.rating != null ? (
          <StarRatingDisplay rating={thread.rating} />
        ) : (
          thread.customerHandle && (
            <p className="text-xs text-muted-foreground mb-0.5">{thread.customerHandle}</p>
          )
        )}
        <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
          {thread.preview || "No message preview"}
        </p>
        {thread.platform === "eatzy" && thread.hasReply && (
          <div className="flex items-center gap-1 mt-1">
            <CheckCircle2 className="h-3 w-3 text-green-500" />
            <span className="text-xs text-green-600">Replied</span>
          </div>
        )}
      </div>
    </button>
  );
}

function EatzyThreadDetail({
  thread,
  onBack,
  restaurantId,
}: {
  thread: InboxThread;
  onBack: () => void;
  restaurantId: string;
}) {
  const { toast } = useToast();
  const [replyText, setReplyText] = useState("");

  const { data: threadData, isLoading } = useQuery<EatzyThreadDetailResponse>({
    queryKey: ["/api/partner/inbox/thread", "eatzy", thread.threadId],
    queryFn: async () => {
      const params = new URLSearchParams({ restaurantId });
      const res = await fetch(`/api/partner/inbox/thread/eatzy/${thread.threadId}?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token") || ""}`,
          "x-eatzy-token": sessionStorage.getItem("partnerEatzyToken") || "",
        },
      });
      if (!res.ok) throw new Error("Failed to load review");
      return res.json();
    },
    staleTime: 30000,
  });

  const replyMutation = useMutation({
    mutationFn: async () => {
      return apiRequest("POST", "/api/partner/inbox/reply", {
        restaurantId,
        platform: "eatzy",
        threadId: thread.threadId,
        message: replyText,
      });
    },
    onSuccess: () => {
      toast({ title: "Reply sent", description: "Your response has been posted." });
      setReplyText("");
      queryClient.invalidateQueries({ queryKey: ["/api/partner/inbox/thread", "eatzy", thread.threadId] });
      queryClient.invalidateQueries({ queryKey: ["/api/partner/inbox"] });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to send",
        description: err.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const reviewData = threadData?.thread;
  const alreadyReplied = !!reviewData?.reply;

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-4 py-3 border-b shrink-0">
        <Button size="icon" variant="ghost" onClick={onBack} data-testid="button-back-inbox">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="relative">
          <Avatar className="h-9 w-9">
            <AvatarImage src={reviewData?.customerAvatar} />
            <AvatarFallback className="text-sm font-semibold">
              {getInitials(thread.customerName)}
            </AvatarFallback>
          </Avatar>
          <span className="absolute -bottom-0.5 -right-0.5 rounded-full bg-background p-0.5">
            <Star className="h-3 w-3 text-primary fill-primary" />
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">{thread.customerName}</p>
          <p className="text-xs text-muted-foreground">Kudoz Review</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
        {isLoading ? (
          <div className="space-y-3">
            <Skeleton className="h-4 w-1/3" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : reviewData ? (
          <>
            <div className="rounded-md bg-muted px-4 py-3 space-y-2">
              <StarRatingDisplay rating={reviewData.rating} />
              {reviewData.text && (
                <p className="text-sm text-foreground">{reviewData.text}</p>
              )}
              <p className="text-xs text-muted-foreground">
                {formatRelativeTime(reviewData.createdAt)}
              </p>
            </div>

            {reviewData.reply && (
              <div className="rounded-md bg-primary/10 px-4 py-3 space-y-1">
                <div className="flex items-center gap-1.5 mb-1">
                  <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                  <span className="text-xs font-medium text-green-700 dark:text-green-400">Your reply</span>
                </div>
                <p className="text-sm text-foreground">{reviewData.reply.text}</p>
                <p className="text-xs text-muted-foreground">
                  {formatRelativeTime(reviewData.reply.createdAt)}
                </p>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground text-center py-6">Could not load review.</p>
        )}
      </div>

      {!alreadyReplied && (
        <div className="border-t px-4 py-3 shrink-0 space-y-2">
          <Textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            placeholder="Write a response to this review..."
            rows={2}
            className="resize-none text-sm"
            data-testid="textarea-inbox-reply"
          />
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={() => replyMutation.mutate()}
              disabled={!replyText.trim() || replyMutation.isPending}
              data-testid="button-send-inbox-reply"
            >
              <Send className="h-3.5 w-3.5 mr-1.5" />
              {replyMutation.isPending ? "Sending…" : "Respond"}
            </Button>
          </div>
        </div>
      )}
      {alreadyReplied && (
        <div className="border-t px-4 py-3 shrink-0">
          <p className="text-xs text-muted-foreground text-center">
            You've already responded to this review.
          </p>
        </div>
      )}
    </div>
  );
}

function SocialThreadDetail({
  thread,
  onBack,
  restaurantId,
}: {
  thread: InboxThread;
  onBack: () => void;
  restaurantId: string;
}) {
  const { toast } = useToast();
  const [replyText, setReplyText] = useState("");

  const { data: threadData, isLoading } = useQuery<SocialThreadDetailResponse>({
    queryKey: ["/api/partner/inbox/thread", thread.platform, thread.threadId],
    queryFn: async () => {
      const params = new URLSearchParams({
        restaurantId,
        connectionId: thread.connectionId || "",
      });
      const res = await fetch(
        `/api/partner/inbox/thread/${thread.platform}/${thread.threadId}?${params}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("auth_token") || ""}`,
            "x-eatzy-token": sessionStorage.getItem("partnerEatzyToken") || "",
          },
        }
      );
      if (!res.ok) throw new Error("Failed to load thread");
      return res.json();
    },
    staleTime: 30000,
  });

  const replyMutation = useMutation({
    mutationFn: async () => {
      // recipientId is no longer sent — server now derives it from threadId via Graph API
      return apiRequest("POST", "/api/partner/inbox/reply", {
        restaurantId,
        platform: thread.platform,
        threadId: thread.threadId,
        message: replyText,
        connectionId: thread.connectionId,
      });
    },
    onSuccess: () => {
      toast({ title: "Reply sent", description: "Your message has been sent." });
      setReplyText("");
      queryClient.invalidateQueries({ queryKey: ["/api/partner/inbox/thread"] });
    },
    onError: (err: Error) => {
      toast({
        title: "Failed to send",
        description: err.message || "Please try again.",
        variant: "destructive",
      });
    },
  });

  const messages: ThreadMessage[] = (() => {
    if (!threadData?.thread) return [];
    if (thread.platform === "facebook") {
      const t = threadData.thread;
      const base: ThreadMessage[] = [{ id: t.id, message: t.message || "", from: t.from, created_time: t.created_time, isPost: true }];
      const comments: ThreadMessage[] = t.comments?.data || [];
      return [...base, ...comments];
    }
    if (thread.platform === "instagram") {
      return (threadData.thread?.messages?.data || []).slice().reverse();
    }
    return [];
  })();

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center gap-3 px-4 py-3 border-b shrink-0">
        <Button size="icon" variant="ghost" onClick={onBack} data-testid="button-back-inbox">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="relative">
          <Avatar className="h-9 w-9">
            <AvatarFallback className="text-sm font-semibold">
              {getInitials(thread.customerName)}
            </AvatarFallback>
          </Avatar>
          <span className="absolute -bottom-0.5 -right-0.5 rounded-full bg-background p-0.5">
            <PlatformIcon platform={thread.platform as "facebook" | "instagram"} size={11} />
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm truncate">{thread.customerName}</p>
          {thread.customerHandle && (
            <p className="text-xs text-muted-foreground">{thread.customerHandle}</p>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {thread.postMessage && thread.platform === "facebook" && (
          <div className="rounded-md bg-muted px-3 py-2 text-xs text-muted-foreground">
            <span className="font-medium block mb-0.5">Your post</span>
            {thread.postMessage}
          </div>
        )}

        {isLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-10 w-3/4" />
            ))}
          </div>
        ) : messages.length > 0 ? (
          messages.map((msg: ThreadMessage, i: number) => {
            const isOurs =
              thread.platform === "instagram"
                ? msg.from?.id === thread.igUserId
                : false;
            return (
              <div
                key={msg.id || i}
                className={`flex ${isOurs ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`max-w-[80%] rounded-xl px-3 py-2 text-sm ${
                    msg.isPost
                      ? "bg-primary/10 text-foreground italic"
                      : isOurs
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-foreground"
                  }`}
                >
                  {!isOurs && msg.from?.name && (
                    <span className="block text-xs font-semibold mb-0.5 text-muted-foreground">
                      {msg.from.name}
                    </span>
                  )}
                  <p>{msg.message}</p>
                  {msg.created_time && (
                    <span className={`block text-xs mt-1 ${isOurs ? "text-primary-foreground/70" : "text-muted-foreground"}`}>
                      {formatRelativeTime(msg.created_time)}
                    </span>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          <p className="text-sm text-muted-foreground text-center py-6">No messages loaded.</p>
        )}
      </div>

      <div className="border-t px-4 py-3 shrink-0 space-y-2">
        <Textarea
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          placeholder="Write a reply..."
          rows={2}
          className="resize-none text-sm"
          data-testid="textarea-inbox-reply"
        />
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={() => replyMutation.mutate()}
            disabled={!replyText.trim() || replyMutation.isPending}
            data-testid="button-send-inbox-reply"
          >
            <Send className="h-3.5 w-3.5 mr-1.5" />
            {replyMutation.isPending ? "Sending…" : "Send"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ThreadDetail({
  thread,
  onBack,
  restaurantId,
}: {
  thread: InboxThread;
  onBack: () => void;
  restaurantId: string;
}) {
  if (thread.platform === "eatzy") {
    return <EatzyThreadDetail thread={thread} onBack={onBack} restaurantId={restaurantId} />;
  }
  return <SocialThreadDetail thread={thread} onBack={onBack} restaurantId={restaurantId} />;
}

export default function PartnerInbox() {
  const restaurantId = localStorage.getItem("partnerRestaurantId") || "";
  const [activePlatform, setActivePlatform] = useState<Platform>("all");
  const [selectedThread, setSelectedThread] = useState<InboxThread | null>(null);

  const {
    data,
    isLoading,
    isFetching,
    refetch,
  } = useQuery<InboxData>({
    queryKey: ["/api/partner/inbox", restaurantId],
    queryFn: async () => {
      const params = new URLSearchParams({ restaurantId });
      const res = await fetch(`/api/partner/inbox?${params}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("auth_token") || ""}`,
          "x-eatzy-token": sessionStorage.getItem("partnerEatzyToken") || "",
        },
      });
      if (!res.ok) throw new Error("Failed to load inbox");
      return res.json();
    },
    enabled: !!restaurantId,
    staleTime: 60000,
  });

  const allThreads: InboxThread[] = data?.threads || [];
  const connected = data?.connected ?? { facebook: false, instagram: false };
  const filteredThreads: InboxThread[] =
    activePlatform === "all"
      ? allThreads
      : allThreads.filter((t) => (t.platform as string) === activePlatform);

  const fbCount = allThreads.filter((t) => t.platform === "facebook").length;
  const igCount = allThreads.filter((t) => t.platform === "instagram").length;
  const eatzyCount = allThreads.filter((t) => t.platform === "eatzy").length;

  // Show "connect" CTA only when the relevant platform(s) are not connected yet
  const showFbConnectCta = activePlatform === "facebook" && !connected.facebook;
  const showIgConnectCta = activePlatform === "instagram" && !connected.instagram;
  const showAllConnectCta =
    activePlatform === "all" && !connected.facebook && !connected.instagram;
  const showConnectCta = showFbConnectCta || showIgConnectCta || showAllConnectCta;

  const platformTabs: { key: Platform; label: string; count?: number; icon?: React.ReactNode }[] = [
    { key: "all", label: "All", count: allThreads.length },
    { key: "facebook", label: "Facebook", count: fbCount, icon: <SiFacebook className="h-3 w-3" /> },
    { key: "instagram", label: "Instagram", count: igCount, icon: <SiInstagram className="h-3 w-3" /> },
    { key: "eatzy", label: "Eatzy", count: eatzyCount, icon: <Star className="h-3 w-3" /> },
  ];

  if (!restaurantId) {
    return (
      <PartnerLayout>
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <MessageSquare className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="font-medium">No restaurant selected</p>
          <p className="text-sm text-muted-foreground">Please select a restaurant to view your inbox.</p>
        </div>
      </PartnerLayout>
    );
  }

  function getEmptyLabel() {
    if (activePlatform === "all") return "No messages yet";
    if (activePlatform === "facebook") return "No Facebook comments";
    if (activePlatform === "instagram") return "No Instagram DMs";
    return "No Eatzy reviews";
  }

  function getEmptyDescription() {
    if (activePlatform === "all")
      return "Customer comments, DMs, and Eatzy reviews will appear here.";
    if (activePlatform === "eatzy")
      return "Reviews left by Kudoz app users for this restaurant will appear here.";
    return "No messages from this platform yet.";
  }

  return (
    <PartnerLayout>
      <div className="flex flex-col h-full -mx-4 -mt-2">
        {selectedThread ? (
          <ThreadDetail
            thread={selectedThread}
            onBack={() => setSelectedThread(null)}
            restaurantId={restaurantId}
          />
        ) : (
          <>
            <div className="flex items-center justify-between px-4 pt-2 pb-3 shrink-0">
              <div>
                <h1 className="text-2xl font-bold" data-testid="text-inbox-title">Inbox</h1>
                <p className="text-sm text-muted-foreground">
                  Messages, comments, and reviews
                </p>
              </div>
              <Button
                size="icon"
                variant="ghost"
                onClick={() => refetch()}
                disabled={isFetching}
                data-testid="button-inbox-refresh"
              >
                <RefreshCw className={`h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
              </Button>
            </div>

            <div className="flex gap-1 px-4 pb-3 shrink-0 overflow-x-auto no-scrollbar">
              {platformTabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActivePlatform(tab.key)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors shrink-0 ${
                    activePlatform === tab.key
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover-elevate"
                  }`}
                  data-testid={`tab-inbox-${tab.key}`}
                >
                  {tab.icon}
                  {tab.label}
                  {tab.count !== undefined && tab.count > 0 && (
                    <Badge
                      className="no-default-active-elevate text-[10px] px-1.5 py-0 h-4 min-w-[1rem]"
                      variant={activePlatform === tab.key ? "secondary" : "outline"}
                    >
                      {tab.count}
                    </Badge>
                  )}
                </button>
              ))}
            </div>

            {activePlatform === "eatzy" && !isLoading && eatzyCount > 0 && (
              <div className="flex justify-end px-4 py-2 border-t shrink-0">
                <Link href="/partner/reviews">
                  <Button variant="ghost" size="sm" className="text-xs" data-testid="button-view-all-reviews-top">
                    <Star className="h-3 w-3 mr-1" />
                    View all reviews
                  </Button>
                </Link>
              </div>
            )}

            <div className="flex-1 overflow-y-auto bg-card border-t">
              {isLoading ? (
                <div className="space-y-px">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="flex items-start gap-3 px-4 py-3.5 border-b">
                      <Skeleton className="h-10 w-10 rounded-full shrink-0" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-3 w-1/3" />
                        <Skeleton className="h-3 w-2/3" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : filteredThreads.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-16 text-center px-6 gap-3">
                  <MessageSquare className="h-12 w-12 text-muted-foreground" />
                  <p className="font-semibold">{getEmptyLabel()}</p>
                  <p className="text-sm text-muted-foreground">{getEmptyDescription()}</p>
                  {showConnectCta && (
                    <Link href="/partner/settings">
                      <Button variant="outline" size="sm" data-testid="button-connect-accounts">
                        <Settings className="h-3.5 w-3.5 mr-1.5" />
                        Connect accounts
                      </Button>
                    </Link>
                  )}
                  {activePlatform === "eatzy" && (
                    <Link href="/partner/reviews">
                      <Button variant="outline" size="sm" data-testid="button-view-all-reviews">
                        <Star className="h-3.5 w-3.5 mr-1.5" />
                        View all reviews
                      </Button>
                    </Link>
                  )}
                </div>
              ) : (
                filteredThreads.map((thread) => (
                  <ThreadCard
                    key={`${thread.platform}-${thread.threadId}`}
                    thread={thread}
                    isSelected={
                      (selectedThread as InboxThread | null)?.platform === thread.platform &&
                      (selectedThread as InboxThread | null)?.threadId === thread.threadId
                    }
                    onClick={() => setSelectedThread(thread)}
                  />
                ))
              )}
            </div>
          </>
        )}
      </div>
    </PartnerLayout>
  );
}
